import type { QueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";

import { CurrentUserHooksHttpRepositoryImpl } from "./repositories/authentication/current-user-hooks-http.repository";
import { EventHooksHttpRepositoryImpl } from "./repositories/events/event-hooks-http.repository";
import { FeatureConfigHooksHttpRepositoryImpl } from "./repositories/feature-config/feature-config-hooks-http.repository";
import { MagicLinkHooksHttpRepositoryImpl } from "./repositories/magic-links/magic-link-hooks-http.repository";
import { ParticipantHooksHttpRepositoryImpl } from "./repositories/participants/participant-hooks-http.repository";
import { HandoffBroadcastHooksHttpRepositoryImpl } from "./repositories/handoff-broadcast/handoff-broadcast-hooks-http.repository";
import { ImportHooksHttpRepositoryImpl } from "./repositories/imports/import-hooks-http.repository";
import { OpenInvitationHooksHttpRepositoryImpl } from "./repositories/open-invitations/open-invitation-hooks-http.repository";
import { DirectoryHooksHttpRepositoryImpl } from "./repositories/directory/directory-hooks-http.repository";
import { AccountHooksHttpRepositoryImpl } from "./repositories/account/account-hooks-http.repository";
import { EventParticipantsAdminHooksHttpRepositoryImpl } from "./repositories/event-participants-admin/event-participants-admin-hooks-http.repository";
import { ParticipantInspectorAdminHooksHttpRepositoryImpl } from "./repositories/participant-inspector-admin/participant-inspector-admin-hooks-http.repository";
import { MatchingEngineAdminHooksHttpRepositoryImpl } from "./repositories/matching-engine-admin/matching-engine-admin-hooks-http.repository";
import { UserEnrichmentAdminHooksHttpRepositoryImpl } from "./repositories/user-enrichment-admin/user-enrichment-admin-hooks-http.repository";
import { CompanyEnrichmentAdminHooksHttpRepositoryImpl } from "./repositories/company-enrichment-admin/company-enrichment-admin-hooks-http.repository";
import { NetworkAdminHooksHttpRepositoryImpl } from "./repositories/network-admin/network-admin-hooks-http.repository";
import { DashboardHooksHttpRepositoryImpl } from "./repositories/dashboard/dashboard-hooks-http.repository";
import { DashboardAdminHooksHttpRepositoryImpl } from "./repositories/dashboard-admin/dashboard-admin-hooks-http.repository";
import { ConnectionsHooksHttpRepositoryImpl } from "./repositories/connections/connections-hooks-http.repository";
import { ConnectionRequestsHooksHttpRepositoryImpl } from "./repositories/connection-requests/connection-requests-hooks-http.repository";
import { ParticipantRosterHooksHttpRepositoryImpl } from "./repositories/participant-roster/participant-roster-hooks-http.repository";
import { EventHostHooksHttpRepositoryImpl } from "./repositories/event-hosts/event-host-hooks-http.repository";
import { UsersAdminHooksHttpRepositoryImpl } from "./repositories/users-admin/users-admin-hooks-http.repository";
import { EventUsersHooksHttpRepositoryImpl } from "./repositories/event-users/event-users-hooks-http.repository";

export interface FrontendDALDependencies {
  readonly queryClient: QueryClient;
  readonly apiClient: AxiosInstance;
}

export class FrontendDAL {
  public readonly auth;
  public readonly events;
  public readonly featureConfig;
  public readonly magicLinks;
  public readonly participants;
  public readonly handoffBroadcast;
  public readonly imports;
  public readonly openInvitations;
  public readonly directory;
  public readonly account;
  public readonly eventParticipantsAdmin;
  public readonly participantInspectorAdmin;
  public readonly matchingEngineAdmin;
  public readonly userEnrichmentAdmin;
  public readonly companyEnrichmentAdmin;
  public readonly networkAdmin;
  public readonly dashboard;
  public readonly dashboardAdmin;
  public readonly connections;
  public readonly connectionRequests;
  public readonly participantRoster;
  public readonly eventHosts;
  public readonly usersAdmin;
  public readonly eventUsers;

  constructor(deps: FrontendDALDependencies) {
    const { queryClient: qc, apiClient: ac } = deps;
    this.auth = new CurrentUserHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.events = new EventHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.featureConfig = new FeatureConfigHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.magicLinks = new MagicLinkHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.participants = new ParticipantHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.handoffBroadcast = new HandoffBroadcastHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.imports = new ImportHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.openInvitations = new OpenInvitationHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.directory = new DirectoryHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.account = new AccountHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.eventParticipantsAdmin =
      new EventParticipantsAdminHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.participantInspectorAdmin =
      new ParticipantInspectorAdminHooksHttpRepositoryImpl(
        qc,
        ac,
      ).createHooks();
    this.matchingEngineAdmin = new MatchingEngineAdminHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.userEnrichmentAdmin = new UserEnrichmentAdminHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.companyEnrichmentAdmin =
      new CompanyEnrichmentAdminHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.networkAdmin = new NetworkAdminHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.dashboard = new DashboardHooksHttpRepositoryImpl(qc, ac).createHooks();
    this.dashboardAdmin = new DashboardAdminHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.connections = new ConnectionsHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.connectionRequests = new ConnectionRequestsHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.participantRoster = new ParticipantRosterHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.eventHosts = new EventHostHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.usersAdmin = new UsersAdminHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
    this.eventUsers = new EventUsersHooksHttpRepositoryImpl(
      qc,
      ac,
    ).createHooks();
  }
}

let dalInstance: FrontendDAL | null = null;

export function configureFrontendDAL(deps: FrontendDALDependencies): void {
  dalInstance = new FrontendDAL(deps);
}

const DAL_NOT_CONFIGURED_MESSAGE =
  "FrontendDAL not configured. Call configureFrontendDAL() during app initialization.";

class FrontendDALNotConfiguredError extends Error {
  constructor() {
    super(DAL_NOT_CONFIGURED_MESSAGE);
    this.name = "FrontendDALNotConfiguredError";
  }
}

export function dal(): FrontendDAL {
  if (dalInstance === null) {
    throw new FrontendDALNotConfiguredError();
  }
  return dalInstance;
}
